"use client"

import * as React from "react"
import { animate, utils } from "animejs"
import { cn } from "cn"

import { EmergenceField } from "./emergence-field"
import { LatticeField } from "./lattice-field"
import { useMotionScope } from "../../hooks/use-motion-scope"
import { ease } from "../../lib/motion"

type AmbientVariant = "lattice" | "emergence" | "grid" | "tiles"

const TILE_PULSE_MS = 5200
const TILE_COLUMNS = 12
const TILE_ROWS = 8
const EDGE_FADE_MASK = "radial-gradient(ellipse 90% 80% at 50% 40%, black 30%, transparent 100%)"

function Grid() {
  return (
    <div
      className="absolute inset-0 opacity-50 [background-image:linear-gradient(to_right,var(--color-greyscale-200)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-greyscale-200)_1px,transparent_1px)] [background-size:56px_56px] dark:opacity-40 dark:[background-image:linear-gradient(to_right,var(--color-greyscale-800)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-greyscale-800)_1px,transparent_1px)]"
      style={{ maskImage: EDGE_FADE_MASK, WebkitMaskImage: EDGE_FADE_MASK }}
    />
  )
}

function Tiles() {
  return (
    <div
      className="absolute inset-0 grid gap-3 p-3"
      style={{
        gridTemplateColumns: `repeat(${TILE_COLUMNS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${TILE_ROWS}, minmax(0, 1fr))`,
        maskImage: EDGE_FADE_MASK,
        WebkitMaskImage: EDGE_FADE_MASK,
      }}
    >
      {Array.from({ length: TILE_COLUMNS * TILE_ROWS }, (_, index) => (
        <div
          key={index}
          data-ambient-tile=""
          className="rounded-xl bg-greyscale-200 opacity-0 will-change-[opacity] dark:bg-greyscale-800"
        />
      ))}
    </div>
  )
}

function AmbientField({ variant = "lattice", className }: { variant?: AmbientVariant; className?: string }) {
  const { root } = useMotionScope<HTMLDivElement>(
    ({ root, reduced }) => {
      const tiles = Array.from(root.querySelectorAll("[data-ambient-tile]"))
      if (reduced) {
        utils.set(tiles, { opacity: () => utils.random(0, 0.35, 2) })
        return
      }
      if (tiles.length > 0) {
        animate(tiles, {
          opacity: [0, 0.45, 0],
          duration: TILE_PULSE_MS,
          delay: () => utils.random(0, TILE_PULSE_MS * 3),
          loopDelay: TILE_PULSE_MS * 2,
          ease: ease.inOut,
          loop: true,
        })
      }
    },
    [variant]
  )

  return (
    <div
      ref={root}
      data-slot="ambient-field"
      data-variant={variant}
      aria-hidden="true"
      className={cn("pointer-events-none fixed inset-0 -z-10 overflow-hidden", className)}
    >
      {variant === "lattice" && <LatticeField />}
      {variant === "emergence" && <EmergenceField />}
      {variant === "grid" && <Grid />}
      {variant === "tiles" && <Tiles />}
    </div>
  )
}

export { AmbientField, type AmbientVariant }
